import Imgix from "../core/Imgix";
import Swiper from "swiper";
import { debounce } from "../utility/UtilityFunctions";
import {
    Autoplay,
    Navigation,
    Pagination,
    Keyboard,
    Mousewheel,
    Scrollbar,
} from "swiper/modules";

export default class Slider {
    constructor(el) {
        this.element = el;
        this.parent = el.parentElement;
        this.numberOfSlides = this.element.querySelectorAll(
            ".swiper-wrapper > .swiper-slide"
        ).length;
        this.imagesRequested = false;
        this.swiper = null;
        this.imgixInstance = new Imgix(); // register new Imgix instance

        // Bind debounced resize handler
        this.onResize = debounce(this.checkSlideCountAndWarn.bind(this), 250);

        this.options = this.getDefaultOptions();
        this.applyCustomOptions();
        this.initSlider();

        // Register resize event
        window.addEventListener("resize", this.onResize);
    }

    getDefaultOptions() {
        let navigationPrev = this.parent.querySelector(".swiper-button-prev");
        let navigationNext = this.parent.querySelector(".swiper-button-next");
        let paginationEl = this.parent.querySelector(".swiper-pagination");

        const options = {
            modules: [Navigation, Pagination, Keyboard, Mousewheel],
            loop: true,
            preventInteractionOnTransition: true, // ! be careful this isn't a breaking addition (prevents slide skips for partial slides (1.2 perView etc))
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
            slidesPerView: "auto",
            mousewheel: {
                forceToAxis: true, // Only scrolls on the intended axis (prevents weird diagonal behavior)
                sensitivity: 0.5, // Reduces scroll sensitivity (default is 1). Lower = smoother.
                releaseOnEdges: true, // Allows page scrolling when at Swiper edge
                thresholdDelta: 10, // Minimum scroll delta to trigger
                thresholdTime: 200, // Throttle scroll events (ms)
            },
            on: {
                slideChange: (swiper) => this.handleSlideChange(swiper),

                // ! DEBUGGING FUNCTIONS
                // init: (swiper) => this.debugSwiper(swiper),
                // slideChange: (swiper) => console.log('Slide changed to:', swiper.activeIndex),
                // navigationNext: () => console.log('Next button clicked'), // triggers twice on click
                // navigationPrev: () => console.log('Prev button clicked'), // triggers twice on click
            },
        };

        if (navigationPrev && navigationNext) {
            options.navigation = {
                prevEl: navigationPrev,
                nextEl: navigationNext,
            };
        }

        if (paginationEl) {
            options.pagination = {
                el: paginationEl,
                clickable: true,
            };
        }

        return options;
    }

    checkSlideCountAndWarn() {
        const slideCount = this.element.querySelectorAll(".swiper-slide").length;
        const breakpoints = this.options.breakpoints || {};
        const viewportWidth = window.innerWidth;

        // Determine effective slidesPerView
        let slidesPerView =
            this.options?.slidesPerView === "auto"
                ? 1
                : this.options?.slidesPerView || 1;

        // Compare against breakpoint slidesPerView if set
        for (const breakpoint in breakpoints) {
            const bp = parseInt(breakpoint, 10);
            if (
                viewportWidth >= bp &&
                breakpoints[bp].slidesPerView !== undefined
            ) {
                slidesPerView = breakpoints[bp].slidesPerView;
            }
        }

        // Force slidesPerView to an integer by truncating decimal part
        slidesPerView = parseInt(slidesPerView, 10);

        const notEnoughSlides = slideCount < Math.ceil(slidesPerView) + 1;
        this.element.classList.toggle(
            "swiper--not-enough-slides",
            notEnoughSlides
        );
    }

    applyCustomOptions() {
        const classList = this.parent.classList;
        const scrollbarEl = this.parent.querySelector(".swiper-scrollbar");

        let configUpdates = {};

        switch (true) {
            case classList.contains("c-hero"):
                configUpdates = {
                    modules: [ Autoplay, Navigation, Pagination, Keyboard, Mousewheel ],
                    autoplay: {
                        delay: 5000,
                        disableOnInteraction: true,
                    },
                    on: {
                        navigationPrev: (swiper) =>
                            this.onUserInteracted(swiper),
                        navigationNext: (swiper) =>
                            this.onUserInteracted(swiper),
                        paginationClick: (swiper) =>
                            this.onUserInteracted(swiper),
                        keypress: (swiper) => this.onUserInteracted(swiper),
                        touchStart: (swiper) => this.onUserInteracted(swiper),
                        sliderFirstMove: (swiper) =>
                            this.onUserInteracted(swiper),
                        interactionStart: (swiper) =>
                            this.onUserInteracted(swiper),
                    },
                };
                break;

            case classList.contains("c-userflow"):
                configUpdates = {
                    modules: [Navigation, Keyboard, Scrollbar, Mousewheel],
                    loop: false,
                    centeredSlides: false,
                    slidesPerView: 1.2,
                    spaceBetween: 16,
                    pagination: {
                        enabled: false,
                    },
                    breakpoints: {
                        765: {
                            slidesPerView: 2,
                            spaceBetween: 24,
                        },
                        1024: { slidesPerView: 2.5 },
                    },
                };
                break;
            case classList.contains("c-benefits-slider"):
                // add new options
                configUpdates = {
                    modules: [Navigation, Pagination, Autoplay],
                    speed: 2500,
                    loop: true,
                    autoplay: {
                        delay: 1000,
                        disableOnInteraction: false,
                    },
                    navigation: {
                        nextEl: null,
                        prevEl: null,
                    },
                    pagination: {
                        el: ".swiper-pagination",
                    },
                };
                break;
            case classList.contains("c-multimedia--autoplay"):
                configUpdates = {
                    modules: [Pagination, Autoplay],
                    speed: 2500,
                    loop: true,
                    autoplay: {
                        delay: 1000,
                        disableOnInteraction: false,
                    },
                    pagination: {
                        el: ".swiper-pagination",
                    },
                };
                break;
            case classList.contains("c-slider"):
                configUpdates = {
                    modules: [Navigation, Keyboard, Scrollbar, Mousewheel],
                    loop: false,
                    centeredSlides: false,
                };
                break;

            case classList.contains("c-instagram-posts"):
                configUpdates = {
                    modules: [Navigation, Keyboard, Scrollbar, Mousewheel],
                    loop: false,
                    centeredSlides: false,
                    slidesPerView: 1.3,
                    spaceBetween: 16,
                    pagination: {
                        enabled: false,
                    },
                    breakpoints: {
                        765: {
                            slidesPerView: 2.3,
                            spaceBetween: 24,
                        },
                        1024: {
                            slidesPerView: 3.3,
                        },
                    },
                };
                break;
        }

        if (scrollbarEl) {
            configUpdates.scrollbar = {
                el: scrollbarEl,
                draggable: true,
                dragSize: "auto",
                hide: false,
            };
        }

        // manually merge on handlers to prevent overwrites due to the assign:
        if (configUpdates.on) {
            this.options.on = {
                ...(this.options.on || {}),
                ...(configUpdates.on || {}),
            };
            delete configUpdates.on; // Prevent Object.assign from overwriting
        }

        Object.assign(this.options, configUpdates);

        const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent
        );

        if (isSafari) {
            // Remove mousewheel options entirely
            delete this.options.mousewheel;

            // Remove Mousewheel module from modules array
            if (Array.isArray(this.options.modules)) {
                this.options.modules = this.options.modules.filter(
                    (m) => m !== Mousewheel
                );
            }
        }
    }

    handleSlideChange(swiper) {
        this.handleVideoPlayback(swiper);

        if (this.imagesRequested) return;

        this.imgixInstance.process(swiper.el);
        this.imagesRequested = true;
    }

    handleVideoPlayback(swiper) {
        const currentSlide = swiper.slides[swiper.activeIndex];
        if (!currentSlide) return;

        const videoElement = currentSlide.querySelector("video");

        swiper.slides.forEach((slide) => {
            const video = slide.querySelector("video");
            if (video && video !== videoElement) video.pause();
        });

        if (videoElement && videoElement.paused) {
            requestAnimationFrame(() => {
                videoElement
                    .play()
                    .catch((error) =>
                        console.warn("Video play() interrupted:", error)
                    );
            });
        }
    }

    onUserInteracted(swiper) {
        if (swiper.el.classList.contains("swiper--interacted")) return;

        swiper.el.classList.add("swiper--interacted");
        swiper.el.style.removeProperty("--slide-delay");
    }

    initSlider() {
        Swiper.use(this.options.modules);
        this.swiper = new Swiper(this.element, this.options);
        this.swiper.init();

        if (
            "autoplay" in this.options ||
            (Array.isArray(this.options.modules) &&
                this.options.modules.includes(Autoplay))
        ) {
            if (this.options?.autoplay?.delay) {
                this.swiper.el.style.setProperty(
                    "--slide-delay",
                    `${this.options.autoplay.delay}ms`
                );
            }

            this.swiper.autoplay.start();
        }
    }

    // ! DEBUGGING FUNCTIONS
    // debugSwiper(swiper) {
    //     // Override slideNext and slidePrev to log when they are called
    //     const originalSlideNext = swiper.slideNext;
    //     const originalSlidePrev = swiper.slidePrev;

    //     swiper.slideNext = (speed, runCallbacks) => {
    //         console.log(`slideNext() called | Speed: ${speed}, Run Callbacks: ${runCallbacks}`);
    //         return originalSlideNext.call(swiper, speed, runCallbacks);
    //     };

    //     swiper.slidePrev = (speed, runCallbacks) => {
    //         console.log(`slidePrev() called | Speed: ${speed}, Run Callbacks: ${runCallbacks}`);
    //         return originalSlidePrev.call(swiper, speed, runCallbacks);
    //     };

    //     // Log all Swiper events
    //     swiper.onAny((eventName, ...args) => {
    //         console.log(`Swiper Event: ${eventName}`, args);
    //     });
    // }
}
